import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaShieldAlt, FaUserShield, FaStar, FaAward, FaFileInvoiceDollar, FaTools } from 'react-icons/fa';
import PageLayout, { CtaBox } from '../../GlobalComp/PageLayout/PageLayout';
import Classes from '../../GlobalComp/PageLayout/PageLayout.module.css';
import Seo, { breadcrumbSchema, localBusinessSchema, serviceSchema } from '../../GlobalComp/Seo/Seo';
import NotFound from '../NotFound/NotFound';
import { getServiceArea } from '../../../data/serviceAreas';
import { SERVICES } from '../../../data/services';
import { SITE } from '../../../data/siteConfig';
import { PHOTOS, altFor, srcSetFor } from '../../../data/photos';

export default function ServiceArea() {
    const { slug } = useParams();
    const area = getServiceArea(slug);
    if (!area) return <NotFound />;

    const path = `/service-areas/${area.slug}`;
    // A different project photo from the hero.
    const [featureImage, ctaImage] = Object.values(PHOTOS).map((p) => p.src).filter((src) => src !== area.image);
    const crumbs = [
        { name: 'Home', path: '/' },
        { name: area.city, path },
    ];
    const description = `Top-rated general contractor, kitchen & bath remodeling and home renovation services in ${area.city}, FL. Licensed & insured (${SITE.license}). Call ${SITE.phone} or book online.`;

    return (
        <>
            <Seo
                title={`General Contractor & Remodeling in ${area.city}, FL | 561-FIX-HOME`}
                description={description}
                path={path}
                jsonLd={[
                    localBusinessSchema(),
                    serviceSchema({ name: `Home Improvement Services in ${area.city}`, description, path, areaName: area.city }),
                    breadcrumbSchema(crumbs),
                ]}
            />
            <PageLayout
                title={`General Contractor & Remodeling in ${area.city}, FL`}
                subtitle={area.intro}
                image={area.image}
            >
                <section>
                    <h2>Services in {area.city}</h2>
                    <div className={Classes.feature}>
                        <div className={Classes.featureMedia}>
                            <img src={featureImage} srcSet={srcSetFor(featureImage)} sizes="(max-width: 900px) 100vw, 600px" alt={altFor(featureImage)} className={Classes.featureImg} loading="lazy" />
                        </div>
                        <div className={Classes.featureBody}>
                            <ul className={Classes.rows}>
                                {SERVICES.map((s) => (
                                    <li key={s.slug}>
                                        <Link to={`/services/${s.slug}`} className={Classes.row}>
                                            <img src={s.icon} alt="" aria-hidden="true" />
                                            <span>
                                                <strong>{s.name}</strong>
                                                <small>{s.summary}</small>
                                            </span>
                                            <span className={Classes.rowArrow} aria-hidden="true">→</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                <section className={Classes.whyBand}>
                    <h2>Why {area.city} Homeowners Choose 561-FIX-HOME</h2>
                    <ul className={Classes.whyGrid}>
                        {[
                            { icon: <FaShieldAlt />, title: 'Licensed Contractor', text: `Florida certified general contractor, license #${SITE.license}.` },
                            { icon: <FaUserShield />, title: 'Fully Insured', text: 'Your home and your project are protected from start to finish.' },
                            { icon: <FaStar />, title: `${SITE.googleRating.value}-Star Rated`, text: `${SITE.googleRating.count} five-star reviews from local homeowners on Google.` },
                            { icon: <FaAward />, title: '20+ Years Experience', text: `Two decades of remodels and repairs in ${area.city} and across South Florida.` },
                            { icon: <FaFileInvoiceDollar />, title: 'Upfront Pricing', text: 'Honest quotes with no surprises: the price we quote is the price you pay.' },
                            { icon: <FaTools />, title: 'Warranty on All Work', text: 'We stand behind every job with a satisfaction guarantee and warranty.' },
                        ].map((f) => (
                            <li key={f.title} className={Classes.whyCard}>
                                <span className={Classes.whyIcon}>{f.icon}</span>
                                <h3>{f.title}</h3>
                                <p>{f.text}</p>
                            </li>
                        ))}
                    </ul>
                    <div className={Classes.bandCta}>
                        <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-dark">Book an Appointment</a>
                    </div>
                </section>

                <CtaBox
                    image={ctaImage}
                    title={`Ready to start your ${area.city} project?`}
                    text={`Book online or call ${SITE.phone} for an honest, upfront quote from a licensed local contractor.`}
                />
            </PageLayout>
        </>
    );
}
